import React, { useState } from "react";
import { Mail, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import SubPageShell from "@/apps/rider/components/SubPageShell";

/**
 * /support/contact — rider sends a free-form support email to ops.
 */
export default function ContactSupport() {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [replyEmail, setReplyEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 2 || message.trim().length < 4) {
      toast.error("Add a subject and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/contact", {
        subject: subject.trim(),
        message: message.trim(),
        reply_email: (replyEmail || "").trim() || undefined,
      });
      setTicket(data?.ticket || null);
      toast.success("Support message sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <SubPageShell eyebrow="Support" title="Contact support"
        subtitle="Sign in to email us — we'll reach you faster with your account on file."
        back="/help" backLabel="Help center" testId="contact-page">
        <Link to="/login" className="inline-flex items-center gap-2 bg-[#0A1128] text-[#F9F7F4] px-5 py-3 rounded-xl text-[12px] tracking-[0.18em] uppercase font-semibold">Sign in</Link>
      </SubPageShell>
    );
  }

  if (ticket) {
    return (
      <SubPageShell eyebrow="Support" title="Message sent" subtitle="A real person will reach out soon."
        back="/help" backLabel="Help center" testId="contact-page">
        <div className="bg-white border hairline rounded-2xl p-6 text-center" data-testid="contact-success">
          <div className="w-12 h-12 mx-auto bg-[#FCEADE] text-[#ed6500] rounded-2xl inline-flex items-center justify-center">
            <CheckCircle2 size={20} />
          </div>
          <div className="font-serif text-xl text-[#0A1128] mt-3">Thanks — we got it</div>
          <p className="text-sm text-[#515E7B] mt-2">
            We typically reply within a few hours during the day.
          </p>
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] mt-4">Ticket</div>
          <div className="font-serif text-lg text-[#0A1128] tracking-wider" data-testid="contact-ticket">{ticket}</div>
          <button
            type="button"
            onClick={() => { setTicket(null); setSubject(""); setMessage(""); }}
            className="mt-5 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#0A1128] hover:text-[#ed6500]"
            data-testid="contact-send-another"
          >
            Send another message
          </button>
        </div>
      </SubPageShell>
    );
  }

  return (
    <SubPageShell
      eyebrow="Support"
      title="Contact support"
      subtitle="Tell us what's up — pricing, account, ride changes, anything."
      back="/help"
      backLabel="Help center"
      testId="contact-page"
    >
      <form onSubmit={submit} className="bg-white border hairline rounded-2xl p-4 space-y-3" data-testid="contact-form">
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] block mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What can we help with?"
            maxLength={160}
            className="input-line w-full"
            data-testid="contact-subject"
            required
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] block mb-1">Reply email</label>
          <input
            type="email"
            value={replyEmail}
            onChange={(e) => setReplyEmail(e.target.value)}
            placeholder="we@reach.you"
            className="input-line w-full"
            data-testid="contact-reply-email"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] block mb-1">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What's on your mind?"
            rows={6}
            maxLength={4000}
            className="w-full bg-[#F9F7F4] border hairline rounded-xl px-3 py-2 text-[14px] text-[#0A1128] placeholder:text-[#515E7B] focus:outline-none focus:border-[#ed6500]/60 resize-none"
            data-testid="contact-message"
            required
          />
          <div className="text-[10px] text-[#515E7B] mt-1 text-right">{message.length}/4000</div>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#0A1128] text-[#F9F7F4] py-3 rounded-xl text-[12px] tracking-[0.2em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="contact-submit"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {submitting ? "Sending…" : "Send message"}
        </button>
      </form>

      <div className="mt-4 text-[12px] text-[#515E7B] inline-flex items-center gap-2">
        <Mail size={14} className="text-[#ed6500]" />
        Or email us directly at{" "}
        <a href="mailto:lamar@lefcoholdings.com" className="text-[#ed6500] hover:underline">lamar@lefcoholdings.com</a>
      </div>
    </SubPageShell>
  );
}
